import React from 'react';

import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, Box, Chip, FilterOptionsState, TextField, Typography } from '@mui/material';

import { ClientResult } from 'stores/ClientStoreAdapter';

// --- 共通Props ---

type ClientSelectBaseProps = {
  /** 全取引先リスト */
  clients: ClientResult[];
  /** ラベル */
  label?: string;
  /** エラー表示 */
  error?: boolean;
  /** エラーメッセージ */
  helperText?: string;
  /** 無効状態 */
  disabled?: boolean;
  /** 必須表示 */
  required?: boolean;
};

// --- 複数選択Props ---

type ClientSelectMultiProps = ClientSelectBaseProps & {
  /** 選択モード: 複数 */
  mode: 'multi';
  /** 選択中の取引先IDリスト */
  selectedClientIds: string[];
  /** 選択変更ハンドラ（複数） */
  onChange: (clientIds: string[]) => void;
};

// --- 単一選択Props ---

type ClientSelectSingleProps = ClientSelectBaseProps & {
  /** 選択モード: 単一 */
  mode: 'single';
  /** 選択中の取引先ID（null=未選択） */
  selectedClientId: string | null;
  /** 選択変更ハンドラ（単一） */
  onChange: (clientId: string | null) => void;
};

export type ClientSelectProps = ClientSelectMultiProps | ClientSelectSingleProps;

/**
 * 取引先選択コンポーネント
 *
 * Autocomplete ベースで取引先を検索・選択可能。
 * mode="multi" で複数選択、mode="single" で単一選択。
 * 取引先名・カナで絞り込みができる。
 */
const ClientSelect: React.FC<ClientSelectProps> = (props) => {
  const {
    clients,
    label = '取引先',
    error = false,
    helperText,
    disabled = false,
    required = false,
  } = props;

  const filterOptions = (options: ClientResult[], state: FilterOptionsState<ClientResult>) => {
    const input = state.inputValue.toLowerCase();
    if (!input) return options;
    return options.filter(
      (c) =>
        c.clientName.toLowerCase().includes(input) ||
        c.clientNameKana.toLowerCase().includes(input),
    );
  };

  const renderOption = (
    optionProps: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
    option: ClientResult,
  ) => {
    const { key, ...rest } = optionProps;
    return (
      <Box
        component="li"
        key={String(key)}
        {...rest}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}
      >
        <BusinessIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {option.clientName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {option.clientNameKana}
            {option.contactName && ` / ${option.contactName}`}
          </Typography>
        </Box>
      </Box>
    );
  };

  if (props.mode === 'single') {
    const selectedClient = clients.find((c) => c.clientId === props.selectedClientId) || null;

    return (
      <Autocomplete
        options={clients}
        value={selectedClient}
        onChange={(_, newValue) => props.onChange(newValue?.clientId || null)}
        getOptionLabel={(option) => option.clientName}
        filterOptions={filterOptions}
        isOptionEqualToValue={(option, value) => option.clientId === value.clientId}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            size="small"
            required={required}
            error={error}
            helperText={helperText}
            placeholder="取引先を検索..."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        )}
        renderOption={renderOption}
        noOptionsText="該当する取引先がありません"
        sx={{ width: '100%' }}
      />
    );
  }

  // mode === 'multi'
  const selectedClients = clients.filter((c) => props.selectedClientIds.includes(c.clientId));

  return (
    <Autocomplete
      multiple
      options={clients}
      value={selectedClients}
      onChange={(_, newValue) => props.onChange(newValue.map((c) => c.clientId))}
      getOptionLabel={(option) => option.clientName}
      filterOptions={filterOptions}
      isOptionEqualToValue={(option, value) => option.clientId === value.clientId}
      disabled={disabled}
      disableCloseOnSelect
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          required={required}
          error={error}
          helperText={helperText}
          placeholder={selectedClients.length === 0 ? '取引先を検索...' : ''}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      )}
      renderOption={renderOption}
      renderTags={(value, getTagProps) =>
        value.map((client, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              {...tagProps}
              label={client.clientName}
              size="small"
              icon={<BusinessIcon sx={{ fontSize: 16 }} />}
              deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              sx={{
                borderRadius: 1.5,
                fontWeight: 500,
                '& .MuiChip-icon': { color: 'primary.main' },
              }}
            />
          );
        })
      }
      noOptionsText="該当する取引先がありません"
      sx={{ width: '100%' }}
    />
  );
};

export default ClientSelect;
